<template>
  <container style="max-width: 950px">
    <v-card outlined>
      <v-card-title>
        {{ $capitalize($tc("model.letter_page_title")) }}
      </v-card-title>
      <v-card-text class="d-flex justify-space-between">
        <div>
          <v-btn
            color="primary"
            small
            class="mr-2 mb-2"
            @click="openCreateLetterDialog"
            >{{ $tc("model.letter_create_button") }}</v-btn
          >
          <v-btn
            color="primary"
            small
            outlined
            class="mb-2"
            @click="handlePrintMany"
            :loading="printing_many"
            :disabled="selected_letters.length == 0"
            >{{ $tc("model.letter_print_many_button") }}</v-btn
          >
        </div>
        <div>
          <v-btn @click="fetchLetters()" icon :loading="fetching">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-menu
            offset-x
            left
            v-model="opened_advanced_filter"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-badge :value="is_filtered" color="red" dot overlap>
                <v-btn v-bind="attrs" v-on="on" icon small>
                  <v-icon>mdi-filter-menu-outline</v-icon>
                </v-btn>
              </v-badge>
            </template>
            <v-form @submit.prevent="handleAdvancedFilter()">
              <v-card>
                <v-card-title>
                  {{ $capitalize($tc("model.letter_advanced_filter_title")) }}
                </v-card-title>
                <v-card-text>
                  <v-simple-table class="filter-table">
                    <tr>
                      <td>
                        <v-select
                          v-model="advanced_filter.letter_type_id"
                          :items="letter_types"
                          :label="
                            $capitalize($tc('model.letter_form_type_label'))
                          "
                          item-text="name"
                          item-value="id"
                          clearable
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <v-autocomplete
                          name="letter.recipient_id"
                          v-model="advanced_filter.recipient_id"
                          :items="advanced_search_contacts_list"
                          :search-input.sync="advanced_search_contacts"
                          no-filter
                          item-text="name"
                          item-value="id"
                          :label="
                            $capitalize(
                              $tc('model.letter_form_recipient_label')
                            )
                          "
                          clearable
                        ></v-autocomplete>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <v-autocomplete
                          v-model="advanced_filter.lead_id"
                          :items="leads"
                          item-text="identifier_code"
                          item-value="id"
                          :label="
                            $capitalize($tc('model.letter_form_lead_label'))
                          "
                          clearable
                        ></v-autocomplete>
                      </td>
                    </tr>
                  </v-simple-table>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="handleResetFilter" text>{{
                    $capitalize(
                      $tc("model.letter_advanced_filter_button_clean")
                    )
                  }}</v-btn>
                  <v-btn type="submit" color="primary">{{
                    $capitalize(
                      $tc("model.letter_advanced_filter_buttons_search")
                    )
                  }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </v-menu>
        </div>
      </v-card-text>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="letters"
          :options.sync="options"
          :server-items-length="totalItems"
          :loading="fetching"
          :disabled="fetching"
        >
          <template v-slot:item.selected="{ item }">
            <v-checkbox
              v-model="selected_letters"
              :value="item.id"
            ></v-checkbox>
          </template>
          <template v-slot:item.lead_number="{ item }">
            <v-chip
              v-if="item.lead_number"
              :href="generatedLeadLink(item.lead_number)"
              target="_blank"
              link
            >
              <v-icon small left>mdi-open-in-new</v-icon>
              {{ item.lead_number }}
            </v-chip>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-menu offset-y transition="slide-y-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-on="on" v-bind="attrs">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <scope-provider scope="letters.print">
                  <v-list-item :href="item.document_url" target="_blank">
                    <v-icon left size="20">mdi-file-find</v-icon>
                    <v-list-item-title>{{
                      $capitalize($tc("model.letter_print_button"))
                    }}</v-list-item-title>
                  </v-list-item>
                </scope-provider>
                <scope-provider scope="letters.send">
                  <v-list-item @click="confirmSend(item.id)">
                    <v-icon left size="20">mdi-send</v-icon>
                    <v-list-item-title>{{
                      $capitalize($tc("model.letter_send_button"))
                    }}</v-list-item-title>
                  </v-list-item>
                </scope-provider>
                <scope-provider scope="letters.delete">
                  <v-list-item value="true" @click="confirmDelete(item.id)">
                    <v-icon left size="20">mdi-delete</v-icon>
                    <v-list-item-title>{{
                      $capitalize($tc("model.letter_remove_button"))
                    }}</v-list-item-title>
                  </v-list-item>
                </scope-provider>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <confirm-action-dialog
      :confirm_action="send_dialog"
      :message="$tc('model.letter_confirm_send_message')"
      @change="send_dialog = $event"
      @action="handleSend"
    />
    <confirm-action-dialog
      :confirm_action="delete_dialog"
      :message="$tc('model.letter_confirm_delete_message')"
      @change="delete_dialog = $event"
      @action="handleDelete"
    />
    <v-dialog v-model="create_dialog" persistent max-width="700px">
      <v-card>
        <v-form @submit.prevent="handleNewLetter">
          <v-card-title>
            {{ $tc("model.letter_create_letter_dialog_title") }}
            <v-spacer />
            <v-btn icon @click="closeCreateLetterDialog"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-card-title>
          <v-card-text>
            <ValidationObserver ref="form_new_letter">
              <ValidationProvider
                name="letter.letter_type_id"
                rules="required"
                v-slot="{ errors }"
              >
                <v-select
                  name="letter.letter_type_id"
                  v-model="form_new_letter.letter_type_id"
                  :items="letter_types"
                  :label="$capitalize($tc('model.letter_form_type_label'))"
                  item-text="name"
                  item-value="id"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <ValidationProvider
                name="letter.recipient_id"
                rules="required"
                v-slot="{ errors }"
              >
                <select-contact
                  v-model="form_new_letter.recipient"
                  :label="$capitalize($tc('model.letter_form_recipient_label'))"
                  :placeholder="
                    $capitalize($tc('model.letter_search_contacts_placeholder'))
                  "
                  item-text="name"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <ValidationProvider name="letter.lead_id" v-slot="{ errors }">
                <v-autocomplete
                  name="letter.lead_id"
                  v-model="form_new_letter.lead_id"
                  :items="leads"
                  item-text="identifier_code"
                  item-value="id"
                  :label="$capitalize($tc('model.letter_form_lead_label'))"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </ValidationObserver>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" type="submit" :loading="submitting">
              {{ $capitalize($tc("model.interface_save_button")) }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </container>
</template>

<script>
import { mapActions } from "vuex";
import { formatDate } from "@/tools/date";
import {
  index as indexLetters,
  destroy as destroyLetter,
  send as sendLetter,
  store as storeLetter,
  printMany as printManyLetters
} from "@/services/letters";
import { my_leads } from "@/services/lead";
import SelectContact from "@/components/SelectContact";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import ScopeProvider from "@/components/ScopeProvider";

export default {
  components: {
    ScopeProvider,
    ConfirmActionDialog,
    SelectContact
  },
  data: function() {
    return {
      // Confirmation Action
      delete_dialog: false,
      delete_letter_id: null,
      send_dialog: false,
      send_letter_id: null,

      // New Letter Dialog
      create_dialog: false,
      submitting: false,
      form_new_letter: {
        letter_type_id: null,
        recipient_id: null,
        recipient: null,
        lead_id: null
      },

      // Main
      fetching: false,
      printing_many: false,
      headers: [
        {
          text: "",
          value: "selected",
          sortable: false
        },
        {
          text: this.$capitalize(this.$tc("model.letter_header_type")),
          value: "type.name",
          sortable: false
        },
        {
          text: this.$capitalize(this.$tc("model.letter_header_recipient")),
          value: "recipient_name"
        },
        {
          text: this.$capitalize(this.$tc("model.letter_header_lead")),
          value: "lead_number"
        },
        {
          text: this.$capitalize(this.$tc("model.letter_header_created_at")),
          value: "created_at"
        },
        {
          text: this.$capitalize(this.$tc("model.letter_header_actions")),
          value: "actions",
          sortable: false
        }
      ],
      totalItems: 0,
      options: {
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      letters: [],
      selected_letters: [],

      leads: [],
      fetching_leads: false,

      //Advanced Filter
      opened_advanced_filter: false,
      advanced_filter: {
        letter_type_id: null,
        recipient_id: null,
        lead_id: null
      },
      advanced_search_fetchings_contacts: false,
      advanced_search_contacts: null,
      advanced_search_contacts_list: []
    };
  },
  methods: {
    // New Letter Dialog
    openCreateLetterDialog() {
      this.create_dialog = true;
    },
    closeCreateLetterDialog() {
      this.create_dialog = false;
      this.form_new_letter = {
        letter_type_id: null,
        recipient_id: null,
        recipient: null,
        lead_id: null
      };
      this.$refs.form_new_letter.reset();
    },

    // Confirmation Action
    confirmSend(letter_id) {
      this.send_dialog = true;
      this.send_letter_id = letter_id;
    },
    confirmDelete(letter_id) {
      this.delete_dialog = true;
      this.delete_letter_id = letter_id;
    },

    // Actions
    async handleNewLetter() {
      const validation = await this.$refs.form_new_letter.validate();
      if (!validation) return;

      this.submitting = true;
      try {
        const response = await storeLetter({
          letter: {
            ...this.form_new_letter,
            recipient_id: this.form_new_letter.recipient?.id
          }
        });
        this.fetchLetters();
        this.closeCreateLetterDialog();
      } catch (error) {
        this.$refs.form_new_letter.setErrors(error.response.data.errors);
        this.$store.commit("sendMessage", {
          text: error.response.data.message,
          color: "red"
        });
      } finally {
        this.submitting = false;
      }
    },
    async handlePrintMany() {
      this.printing_many = true;
      try {
        const response = await printManyLetters(this.selected_letters);
        const blob = new Blob([response], { type: "application/pdf" });
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl);
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: this.$tc("model.letter_print_many_error_message"),
          color: "red"
        });
      } finally {
        this.printing_many = false;
      }
    },
    async handleSend() {
      try {
        await sendLetter(this.send_letter_id);
        this.$store.commit("sendMessage", {
          text: this.$tc("model.letter_remove_success_message")
        });
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: this.$tc("model.letter_remove_error_message"),
          color: "red"
        });
      } finally {
        this.send_dialog = false;
        this.send_letter_id = null;
      }
    },
    async handleDelete() {
      try {
        await destroyLetter(this.delete_letter_id);
        this.$store.commit("sendMessage", {
          text: this.$tc("model.letter_remove_success_message")
        });
        this.fetchLetters();
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: this.$tc("model.letter_remove_error_message"),
          color: "red"
        });
      } finally {
        this.delete_dialog = false;
        this.delete_letter_id = null;
      }
    },
    async fetchLetters() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.fetching = true;
      try {
        const response = await indexLetters({
          orderBy: sortBy[0] ? sortBy[0] : "",
          sortDesc: sortDesc[0] ? true : false,
          page,
          perPage: itemsPerPage,
          ...this.advanced_filter
        });
        this.letters = response.data;
        this.totalItems = response.total;
      } finally {
        this.fetching = false;
      }
    },
    async fetchMyLeads() {
      this.fetching_leads = true;
      this.leads = [];

      const response = await my_leads();

      this.leads = response.data;
      this.fetching_leads = false;
    },
    generatedLeadLink(lead_number) {
      const route = this.$router.resolve({
        name: "lead_detail_files",
        params: { lead_number }
      });

      return route.href;
    },
    formatDate(date) {
      return formatDate(date);
    },

    // Advanced Filter
    handleAdvancedFilter(value) {
      this.fetchLetters();
      this.opened_advanced_filter = false;
    },
    handleResetFilter(value) {
      this.advanced_filter = {
        letter_type_id: null,
        recipient_id: null,
        lead_id: null
      };
      this.filtered = false;
      this.fetchLetters();
    },
    async fetchAdvancedFilterContacts(filter = "") {
      if (this.advanced_search_fetchings_contacts) return;

      this.advanced_search_fetchings_contacts = true;
      const response = await this.$http.get("/contacts", {
        params: {
          filter
        }
      });
      this.advanced_search_contacts_list = response.data.data;
      this.advanced_search_fetchings_contacts = false;
    },
    ...mapActions("lead_statuses", ["loadStatuses"]),
    ...mapActions("letter_types", ["loadLetterTypes"])
  },
  computed: {
    letter_types() {
      return this.$store.getters["letter_types/getLetterTypes"];
    },
    lead_statuses() {
      return this.$store.getters["lead_statuses/getLeadStatuses"];
    },
    is_filtered() {
      return (
        !!this.advanced_filter.letter_type_id ||
        !!this.advanced_filter.recipient_id ||
        !!this.advanced_filter.lead_id
      );
    }
  },
  created() {
    this.fetchAdvancedFilterContacts();
    this.loadStatuses().finally(() => {
      this.fetchMyLeads();
    });
    this.loadLetterTypes();
    this.fetchLetters();
  },
  watch: {
    options: {
      handler() {
        this.fetchLetters();
      },
      deep: true
    },
    filter: function(value) {
      if (value == null) this.fetchLetters();
    },
    search_contacts: function(value) {
      this.fetchContacts(value);
    },
    advanced_search_contacts: function(value) {
      this.fetchAdvancedFilterContacts(value);
    }
  }
};
</script>

<style></style>
